import React from 'react';

import { cn } from '../../lib/utils';

interface ChatHeaderProps {
  agentName: string;
  agentColour: string;
  coreMessage: string;
  sessionTitle?: string;
  /** Hint text shown below the header (e.g. for Early Testers) */
  hint?: string;
  className?: string;
}

/** All 7 agent colours in order for the prism visual */
const AGENT_COLOURS = [
  '#C75B5B', // Red
  '#D4874D', // Orange
  '#C9A84C', // Yellow
  '#5B9E6F', // Green
  '#4D9E9E', // Turquoise
  '#4A7FB5', // Blue
  '#7B5EA7', // Purple
];

/** Agent synopsis and inspirational quote, keyed by lowercase agent name */
const AGENT_QUOTES: Record<string, { colourLabel: string; synopsis: string; quote: string; author: string }> = {
  ray: { colourLabel: 'Red', synopsis: 'preparation', quote: 'By failing to prepare, you are preparing to fail.', author: 'Benjamin Franklin' },
  olympia: { colourLabel: 'Orange', synopsis: 'ignition', quote: 'Enthusiasm is the mother of effort, and without it nothing great was ever achieved.', author: 'Ralph Waldo Emerson' },
  yves: { colourLabel: 'Yellow', synopsis: 'steady progress', quote: 'Start where you are. Use what you have. Do what you can.', author: 'Arthur Ashe' },
  gail: { colourLabel: 'Green', synopsis: 'collective strength', quote: 'The strength of the team is each individual member. The strength of each member is the team.', author: 'Phil Jackson' },
  timothy: { colourLabel: 'Turquoise', synopsis: 'meaningful impact', quote: 'What you do makes a difference, and you have to decide what kind of difference you want to make.', author: 'Jane Goodall' },
  byron: { colourLabel: 'Blue', synopsis: 'intentional creation', quote: 'The best way to predict the future is to create it.', author: 'Peter Drucker' },
  peter: { colourLabel: 'Purple', synopsis: 'contribution', quote: 'The purpose of life is to contribute in some way to making things better.', author: 'Robert F. Kennedy' },
};

/**
 * Chat header showing a mini prism visual (active agent's ray highlighted),
 * the agent's name with colour dot, and core message subtitle.
 */
export function ChatHeader({
  agentName,
  agentColour,
  coreMessage,
  sessionTitle,
  hint,
  className,
}: ChatHeaderProps): React.JSX.Element {
  const agentQuote = AGENT_QUOTES[agentName.toLowerCase()];

  return (
    <header
      className={cn(
        'flex flex-col border-b border-border bg-white flex-shrink-0',
        className,
      )}
    >
      <div className="flex items-center gap-4 px-6 py-3">
        {/* Mini prism with active agent highlighted */}
        <AgentPrism activeColour={agentColour} />

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <h1 className="text-lg font-semibold text-text-primary truncate">
              {agentName}
            </h1>
            {sessionTitle && (
              <span className="text-sm text-text-muted truncate">— {sessionTitle}</span>
            )}
          </div>
          <p className="text-sm text-text-secondary">{coreMessage}</p>
        </div>
      </div>
      {agentQuote && (
        <p className="px-6 pb-2 text-xs leading-relaxed text-text-secondary">
          <span style={{ color: agentColour }} className="font-semibold">{agentQuote.colourLabel}</span>
          {' → '}{agentQuote.synopsis}: <span className="italic">&ldquo;{agentQuote.quote}&rdquo;</span> — {agentQuote.author}
        </p>
      )}
      {hint && (
        <p className="px-6 pb-2 text-xs text-text-muted italic">{hint}</p>
      )}
    </header>
  );
}

/**
 * Mini prism SVG — active agent's ray is bold, others are faded.
 */
function AgentPrism({ activeColour }: { activeColour: string }) {
  // Ray start points (exiting from prism right edge) and end points
  const rays = [
    { x1: 42, y1: 10, x2: 70, y2: 3 },
    { x1: 43, y1: 13, x2: 70, y2: 10 },
    { x1: 44, y1: 16, x2: 70, y2: 17 },
    { x1: 44, y1: 19, x2: 70, y2: 24 },
    { x1: 43, y1: 22, x2: 70, y2: 31 },
    { x1: 42, y1: 25, x2: 70, y2: 38 },
    { x1: 41, y1: 28, x2: 70, y2: 45 },
  ];

  return (
    <svg
      width="70"
      height="48"
      viewBox="0 0 70 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="flex-shrink-0"
    >
      {/* White light entering */}
      <line x1="0" y1="20" x2="22" y2="20" stroke="#1D1C1D" strokeWidth="1.5" strokeLinecap="round" />
      {/* Prism */}
      <polygon points="24,6 42,6 33,40" fill="none" stroke="#1D1C1D" strokeWidth="1" />
      {/* 7 coloured rays — active one is bold */}
      {rays.map((ray, i) => {
        const colour = AGENT_COLOURS[i]!;
        const isActive = colour === activeColour;
        return (
          <line
            key={i}
            x1={ray.x1}
            y1={ray.y1}
            x2={ray.x2}
            y2={ray.y2}
            stroke={colour}
            strokeWidth={isActive ? 3 : 1}
            strokeLinecap="round"
            opacity={isActive ? 1 : 0.3}
          />
        );
      })}
    </svg>
  );
}
